import React from "react";
import { View, Pressable, ViewStyle, StyleProp, Platform } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { cn } from "@/lib/utils";

export interface FilterOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

export interface FilterGroupProps {
  options: FilterOption[];
  value: string;
  onChange: (id: string) => void;
  containerStyle?: StyleProp<ViewStyle>;
}

/**
 * A segmented control group for filtering or toggling between options.
 */
export function FilterGroup({ options, value, onChange, containerStyle }: FilterGroupProps) {
  return (
    <View
      className="flex-row items-center gap-1 bg-stroke-30 p-1 border border-stroke-50 rounded-lg flex-wrap"
      style={containerStyle}
      accessibilityRole="radiogroup"
    >
      {options.map((option) => {
        const isSelected = value === option.id;
        return (
          <Pressable
            key={option.id}
            onPress={() => onChange(option.id)}
            accessibilityRole="radio"
            accessibilityState={{ checked: isSelected }}
            className={cn(
              "flex-row items-center gap-1 px-4 py-2 rounded-md",
              isSelected ? "bg-white" : ""
            )}
            style={({ pressed }) => ({
              opacity: pressed && !isSelected ? 0.7 : 1,
            })}
          >
            <AppText
              weight={isSelected ? "semibold" : "medium"}
              className={isSelected ? "text-primary text-base" : "text-supporting-text text-base"}
            >
              {option.label}
            </AppText>
            {option.icon}
          </Pressable>
        );
      })}
    </View>
  );
}
